import React, { useEffect, useState, useRef, useCallback } from "react";
import { Select, Checkbox, Spin, Tag, Tooltip } from "antd";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { updateFilter, updateSelectedItems } from "../../Redux/filterSlice";

const StyledSelect = styled(Select)`
  min-width: 200px;
`;

const FilterSelect = ({ label, fetchOptions, filterKey }) => {
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.filter.filters);
  const [selectedValues, setSelectedValues] = useState(
    filters[filterKey] || []
  );
  const selectedItems = useSelector((state) => state.filter.selectedItems);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const isFetching = useRef(false);

  const loadOptions = useCallback(
    async (pageNumber, query = "") => {
      if (isFetching.current || (!hasMore && pageNumber !== 0)) return;

      isFetching.current = true;
      setLoading(true);

      try {
        const newOptions = await fetchOptions(pageNumber, query);
        setOptions((prevOptions) =>
          pageNumber === 0 ? newOptions : [...prevOptions, ...newOptions]
        );
        setHasMore(newOptions.length > 0);
        setPage(pageNumber);
      } catch (error) {
        console.error("Error fetching options:", error);
      } finally {
        isFetching.current = false;
        setLoading(false);
      }
    },
    [fetchOptions, hasMore]
  );

  useEffect(() => {
    if (!hasMore) return;
    if (!isFetching.current) {
      const handler = setTimeout(() => {
        loadOptions(0, searchQuery);
      }, 1000);
      return () => clearTimeout(handler);
    }
  }, [loadOptions, searchQuery, hasMore]);

  const handleChange = (values) => {
    setSelectedValues(values);
    dispatch(updateFilter({ key: filterKey, values }));

    const newSelectedItems = options.filter(item =>
      values.includes(item.value)
    );

    if (newSelectedItems.length === 0) {
      dispatch(updateSelectedItems({ key: filterKey, items: [] }));
      return;
    }
    
    let mergeArray = [
      ...(selectedItems[filterKey] || []),   
      ...newSelectedItems            
    ].filter((item, index, self) =>
      index === self.findIndex(t => t.value === item.value)
    );

    mergeArray = mergeArray.filter(item => values.includes(item.value));

    setSearchQuery("")
    dispatch(updateSelectedItems({ key: filterKey, items: mergeArray }));
  };


  const customTagRender = (props) => (
    <Tag color="purple" key={`${filterKey}-selected`} {...props}>
      {`${selectedValues.length} ${label} Selected`}
    </Tag>
  );

  const mergedOptions = [
    ...(selectedItems[filterKey] || [])?.map(value => {
      const foundOption = options.find(option => option.value === value.value);
      return foundOption || { value :value?.value, label: value?.label }; // Ensure selected items always appear
    }),
    ...options?.filter(option => !selectedValues.includes(option.value))
  ];


  return (
    <StyledSelect
      mode="multiple"
      placeholder={`Select ${label}`} // ✅ Fixed string interpolation
      value={selectedValues}
      onFocus={() => {
        // Clear search term and reload options when the field is focused
        if(searchQuery !== ""){
          setSearchQuery("");
          loadOptions(0, "");
        }
      }}
      onSearch={(value) => {
        setSearchQuery(value);
        setPage(0);
        setHasMore(true);
      }}
      showSearch
      onChange={handleChange}
      onPopupScroll={(e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollTop + clientHeight >= scrollHeight - 20) {
          loadOptions(page + 1, searchQuery);
        }
      }}
      maxTagCount="responsive" // ✅ Prevents long lists from breaking UI
      loading={loading}
      filterOption={false}
      tagRender={customTagRender} // ✅ Uses custom tags
      allowClear
      menuItemSelectedIcon={null}
    >
      {mergedOptions.map((option) => (
        <Select.Option key={option.value} value={option.value}>
          <Checkbox checked={selectedValues.includes(option.value)} />{" "}
          <Tooltip title={option.label}>
          {option.label}
          </Tooltip>
        </Select.Option>
      ))}
      {loading && (
        <Select.Option key="loading" disabled>
          <Spin size="small" /> Loading...
        </Select.Option>
      )}
    </StyledSelect>
  );
};

export default FilterSelect;
